/// <reference types="vite/client" />

/**
 * NuGuard Configuration
 * 
 * Environment variables are loaded from .env files via Vite.
 * All env vars must be prefixed with VITE_ to be exposed to the browser.
 */

// Load environment variables (Vite uses import.meta.env with VITE_ prefix)
const getEnv = (key: string, fallback: string): string => {
  // Vite environment (with VITE_ prefix)
  const viteKey = `VITE_${key}` as keyof ImportMetaEnv;
  if (import.meta.env?.[viteKey]) {
    return import.meta.env[viteKey] as string;
  }
  return fallback;
};

export const CONFIG = {
  // API Service URLs (use empty string for relative URLs through gateway)
  DATA_SERVICE_URL: getEnv('DATA_SERVICE_URL', '/api/data'),
  ASSETS_SERVICE_URL: getEnv('ASSETS_SERVICE_URL', '/api/assets'),
  COMPLIANCE_SERVICE_URL: getEnv('COMPLIANCE_SERVICE_URL', '/api/compliance'),
  CHAT_SERVICE_URL: getEnv('CHAT_SERVICE_URL', '/api/chat'),
  REDTEAM_SERVICE_URL: getEnv('REDTEAM_SERVICE_URL', '/api/redteam'),
  BEHAVIOR_SERVICE_URL: getEnv('BEHAVIOR_SERVICE_URL', '/api/behavior'),
  INTEGRATION_SERVICE_URL: getEnv('INTEGRATION_SERVICE_URL', '/api/integrations'),
  LICENSING_SERVICE_URL: getEnv('LICENSING_SERVICE_URL', '/api/licensing'),
  
  // Gemini API Configuration (for direct browser calls in mock mode)
  GEMINI_API_KEY: getEnv('GEMINI_API_KEY', ''),
  GEMINI_MODEL_NAME: getEnv('GEMINI_MODEL_NAME', 'gemini-2.5-flash'),
  GOOGLE_CLOUD_PROJECT: getEnv('GOOGLE_CLOUD_PROJECT', ''),
  VERTEX_AI_LOCATION: getEnv('VERTEX_AI_LOCATION', 'us-central1'),
  
  // Toggle this to false to use real backend, true for demo/preview without backend
  USE_MOCK_MODE: getEnv('USE_MOCK_MODE', 'true') === 'true',

  // Frontend diagnostic logs for troubleshooting preview/runtime issues
  DEBUG_FRONTEND_LOGS: getEnv('DEBUG_FRONTEND_LOGS', 'true') === 'true',

  // Temporary verbose HTTP tracing for red-team/behavior debugging
  DEBUG_SECURITY_TEST_HTTP: getEnv('DEBUG_SECURITY_TEST_HTTP', 'false') === 'true',

  // Context-aware user help system
  ENABLE_CONTEXT_HELP: getEnv('ENABLE_CONTEXT_HELP', 'true') === 'true',

  // Auto-logout after this many minutes with no user interaction (mouse,
  // keyboard, touch, scroll). Set to 0 to disable.
  IDLE_TIMEOUT_MINUTES: Number(getEnv('IDLE_TIMEOUT_MINUTES', '15')),

  // Feature flags
  ENABLE_CHAT_ORCHESTRATOR: getEnv('ENABLE_CHAT_ORCHESTRATOR', 'false') === 'true',
  // Risk Catalog (phase 1): deduplicated behavioral records view. Gates the
  // sidebar item + /risk-catalog routes. Backend counterpart: NUGUARD_RISK_CATALOG_ENABLED.
  ENABLE_RISK_CATALOG: getEnv('ENABLE_RISK_CATALOG', 'false') === 'true',
  // Chatbot source citations (issue #888): inline citation marks + hover
  // preview in assistant replies. Gates whether FloatingAssistant threads
  // citations into MarkdownBody at all — belt-and-suspenders alongside the
  // backend flag, which already never emits markers when off. Backend
  // counterpart: NUGUARD_CHATBOT_CITATIONS_ENABLED.
  ENABLE_CITATIONS: getEnv('ENABLE_CHAT_CITATIONS', 'false') === 'true',
  ENABLE_DEMO_CREDENTIALS: getEnv('ENABLE_DEMO_CREDENTIALS', 'true') === 'true',
  DASHBOARD_V2: getEnv('DASHBOARD_V2', 'true') === 'true',
  // When true, useDashboardV2 serves local fixtures (no backend call).
  // Useful for design review before the v2 endpoint is deployed.
  DASHBOARD_V2_MOCK: getEnv('DASHBOARD_V2_MOCK', 'false') === 'true',
  
  // Auth settings
  TOKEN_STORAGE_KEY: 'nuguard_access_token',
  REFRESH_TOKEN_KEY: 'nuguard_refresh_token',

  // PostHog analytics — https://posthog.com. Leave POSTHOG_KEY empty to no-op
  // (local dev default). Backend counterpart: backend/shared/posthog_analytics.py.
  POSTHOG_KEY: getEnv('POSTHOG_KEY', ''),
  POSTHOG_HOST: getEnv('POSTHOG_HOST', 'https://us.i.posthog.com'),
  // Staging and production share one PostHog project (same key) — this is
  // tagged onto every event as a super property so they can be told apart.
  ENVIRONMENT: getEnv('ENVIRONMENT', 'development'),
};
